import React, { type FC, Fragment, memo, useMemo, useRef } from 'react'
import { Box, checkboxClasses, FormControlLabel, formControlLabelClasses, type SxProps, type Theme } from '@mui/material'
import { SimpleTreeView, TreeItem, treeItemClasses } from '@mui/x-tree-view'
import { ChevronRight, Delete, ExpandMore } from '@mui/icons-material'

import { createEqualityChecker, deltaFromArray, type ExcludeValues } from '../../../../utils'

import {
    areTopicTreesEquals,
    buildTopicTree,
    Capability,
    createIsAuthenticatedWithCapabilitiesSelector,
    findTreeByTopic,
    selectExplorerAreTopicAvailable,
    selectExplorerChunks,
    selectExplorerCustomTopics,
    selectExplorerLoadedChunkPrefixes,
    selectExplorerSelectedTopics,
    type TopicTree,
    Translation,
} from '../../../../domain'

import { createHandlerWithoutPropagation } from '../..'
import { Checkbox, CircularLoader } from '../../common'
import { useOnUnhooked } from '../../Droppable'
import { ErrorMessage } from '../../ErrorMessage'
import { FormattedTranslation, useTranslator } from '../../Internationalization'
import { useAppState, useAppUsecase } from '../../State'
import { PaginationButton } from '../Pagination'
import { ErrorInfoPopover } from './ErrorInfo'

const selectCanReadTopicsMetadata = createIsAuthenticatedWithCapabilitiesSelector(Capability.TOPIC_CHUNKS_READ)
const useAddTopicsChunk = (): ((fullPath: TopicTree['fullPath']) => void) | null => {
    const droppable = useOnUnhooked()
    const loadUsecase = useAppUsecase('explorerChunksUsecase')

    const canReadDeviations = useAppState(selectCanReadTopicsMetadata)

    return canReadDeviations ? (fullPath) => loadUsecase.load(fullPath, droppable) : null
}

/**
 * This deals with topics with a name that is too long
 */
const wrapperStyle: SxProps<Theme> = { width: '100%', display: 'flex' }
const labelStyle: SxProps<Theme> = {
    minWidth: 'calc(100% - 54px)',
    maxWidth: 0,
    [`& > .${checkboxClasses.root}`]: { py: 0 },
    [`& > .${formControlLabelClasses.label}`]: {
        textOverflow: 'ellipsis',
        overflowX: 'hidden',
        whiteSpace: 'nowrap',
        fontSize: 'body2.fontSize',
    },
}
const actionsWrapperStyle: SxProps<Theme> = { width: '100%' }
const actionsStyle: SxProps<Theme> = { display: 'flex', cursor: 'pointer', justifyContent: 'right' }
const treeItemStyle: SxProps<Theme> = { [`& .${treeItemClasses.content}`]: { py: 0, borderRadius: 0 } }

const stopPropagation = createHandlerWithoutPropagation()

type LabelProps = ExcludeValues<Pick<TopicTree, 'fullPath' | 'name' | 'selected' | 'valid' | 'custom'>, null>
const Label = memo<LabelProps>(({ fullPath, name, valid, selected, custom }) => {
    const manage = useAppUsecase('explorerTopicManagementUsecase')
    const translator = useTranslator()

    return (
        <Box sx={wrapperStyle}>
            <FormControlLabel
                sx={labelStyle}
                title={valid ? fullPath : translator.formatTranslation(Translation.INVALID_TOPIC)}
                disabled={!valid}
                control={
                    <Checkbox title={fullPath} checked={selected} size="small" onChange={() => manage.toggleTopics([fullPath])} onClick={stopPropagation} />
                }
                label={name}
            />
            <Box sx={actionsWrapperStyle}>
                <Box sx={actionsStyle}>
                    <ErrorInfoPopover fullPath={fullPath} />

                    {custom && (
                        <Delete
                            color="action"
                            className="remove-custom-topic-icon"
                            fontSize="medium"
                            onClick={createHandlerWithoutPropagation(() => manage.removeCustomTopic(fullPath))}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    )
}, createEqualityChecker<LabelProps>({ fullPath: null, name: null, valid: null, selected: null, custom: null }))

const Tree = memo<Readonly<{ tree: TopicTree }>>(
    ({ tree: { children, isLoadingSibling, loadingSiblingError, fullPath, name, selected, valid, custom, nextPageSize, hasChildrenToLoad } }) => {
        const addTopicsChunk = useAddTopicsChunk()

        const childTrees = [...children.map((tree, k) => <Tree key={k} tree={tree} />)]

        if (isLoadingSibling) {
            childTrees.push(<CircularLoader key="loader" data-testid="topic-chunks-loader" size="1rem" margin="small" />)
        }

        if (loadingSiblingError) {
            childTrees.push(<ErrorMessage key="error" data-testid="topic-chunks-error" titleVariant="body2" error={loadingSiblingError} />)
        }

        if (nextPageSize !== null && addTopicsChunk !== null) {
            childTrees.push(
                <PaginationButton key="addNext" data-testid="topic-chunks-next-page" nextPageSize={nextPageSize} onClick={() => addTopicsChunk(fullPath)} />
            )
        }

        if (!childTrees.length && hasChildrenToLoad) {
            /**
             * Just add a fake child, so the node becomes expandable if it can load children
             */
            childTrees.push(<Fragment key="stub" />)
        }

        if (name === null || fullPath === null) {
            return <>{childTrees}</>
        }

        return (
            <TreeItem
                data-testid={fullPath}
                itemId={fullPath}
                title={name}
                label={<Label name={name} fullPath={fullPath} valid={valid} selected={selected} custom={custom} />}
                sx={treeItemStyle}
            >
                {childTrees}
            </TreeItem>
        )
    },
    (a, b) => areTopicTreesEquals(a.tree, b.tree)
)

/**
 * Utility hook to help figure out what topic was just expanded
 */
const useTopicsList = (): ((updatedNodes: readonly string[]) => string | null) => {
    const visibleNodesRef = useRef<readonly string[]>([])

    return (update) => {
        const [, , [lastAdded = null]] = deltaFromArray(visibleNodesRef.current, update)

        visibleNodesRef.current = update

        return lastAdded
    }
}

export const TopicsTree: FC = () => {
    const allChunks = useAppState(selectExplorerChunks)
    const custom = useAppState(selectExplorerCustomTopics)
    const selected = useAppState(selectExplorerSelectedTopics)

    const available = useAppState(selectExplorerAreTopicAvailable)

    const chunksUsecase = useAppUsecase('explorerChunksUsecase')
    const rootTopic = useMemo(() => chunksUsecase.createRootTopic(), [chunksUsecase])

    const tree = useMemo(() => buildTopicTree(rootTopic, selected, custom, allChunks), [rootTopic, selected, custom, allChunks])

    const updateExpanded = useTopicsList()
    const prefixes = useAppState(selectExplorerLoadedChunkPrefixes)
    const addTopicsChunk = useAddTopicsChunk()

    return (
        <SimpleTreeView
            data-testid="availableTopicsTree"
            slots={{
                collapseIcon: () => <ExpandMore className="collapseTree" />,
                expandIcon: () => <ChevronRight className="expandTree" />,
            }}
            onExpandedItemsChange={(_, nodeIds) => {
                const lastAdded = updateExpanded(nodeIds)

                /**
                 * - Has permissions to add chunks
                 * - Node has been added
                 * - Prefix has not been added before
                 * - And it can load children
                 *
                 * Then add another chunk
                 */
                if (addTopicsChunk && lastAdded && !prefixes.includes(lastAdded) && findTreeByTopic(tree, lastAdded)?.hasChildrenToLoad) {
                    addTopicsChunk(lastAdded)
                }
            }}
        >
            {!available && <FormattedTranslation id={Translation.NO_TOPICS_AVAILABLE} />}
            <Tree tree={tree} />
        </SimpleTreeView>
    )
}
